import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Button, ConfigProvider, Flex, Input, InputNumber, Segmented, Select, Spin, Switch, Tabs, TabsProps, Typography } from "antd";
import { ArrowLeftOutlined, CopyOutlined, DeleteOutlined } from "@ant-design/icons";
import PageHeading from "components/PageHeading.component";
import PATHS from "shared/constants/paths";
import { translate } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatCurrency, getApiSuccessMessage } from "shared/utils";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { pillSegmentedTheme } from "pages/analytics/RangeSegmented";
import { showConfirmModal } from "components/modals/confirmModal";
import { showDestructiveConfirm } from "components/modals/destructiveConfirm";
import { ClassTypeBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { InstructorAvatar } from "pages/schedule/component/InstructorAvatar";
import { InstructorColorProvider } from "context/InstructorColorProvider";
import { ClassCategory, CommissionMode, CommissionRateStructure, UserType } from "shared/types/enum";
import { PayrollClassTier, PayrollRate } from "shared/types";
import { payrollSettingsOptions, payrollClassesOptions, classCommissionDetailOptions, payrollStaffOptions } from "hooks/queries/payroll";
import {
  useUpsertClassCommissionSetting,
  useUpsertClassCommissionTiers,
  useDuplicateClassCommissionSetting,
  useUpsertPayrollRate,
  useDeletePayrollRate,
} from "hooks/mutations/payroll";
import { rateAmountInputProps } from "./currencyInput";
import { percentInputProps } from "./percentInput";
import PayrollSettingsRequired from "./PayrollSettingsRequired";
import { textPx } from "shared/constants/zoom";

// The three commission options a class can be configured with, matching the
// three underlying (mode, rate_structure) axes. "Per Peserta" and
// "Per Pendapatan" each keep their own Flat/Tiered-style sub-choice — "Per
// Kelas" is the only single point. "per_revenue" covers RevenuePercent,
// RevenueTiered, and RevenuePercentTiered, same as "per_attendee" covers
// both Flat and Tiered.
type TopChoice = "per_class" | "per_attendee" | "per_revenue";

const topChoiceOf = (mode?: CommissionMode | null, rateStructure?: CommissionRateStructure | null): TopChoice | null => {
  if (!mode || !rateStructure) return null;
  if (mode === CommissionMode.PerClass) return "per_class";
  if (
    rateStructure === CommissionRateStructure.RevenuePercent ||
    rateStructure === CommissionRateStructure.RevenueTiered ||
    rateStructure === CommissionRateStructure.RevenuePercentTiered
  ) {
    return "per_revenue";
  }
  return "per_attendee";
};

// A class's shared bands, as edited in the browser — structure-agnostic
// (headcount under Tiered, whole-Rupiah revenue under RevenueTiered, see
// the `axis` prop on TierBandsEditor below). `id` is the real
// payroll_class_commission_tier id when this band already exists (null for
// a band added in this editing session, not saved yet) — used only to match
// a row's existing tier_amounts back to the right band on load, never sent
// to the server (a save always POSTs the raw min/max list and gets fresh
// ids back, soft-delete-then-recreate like everything else in this schema).
type ClassTierDraft = { id: string | null; min_value: number | null };

// Only min_value is ever directly entered — max_value is always DERIVED
// from the next tier's min (minus one), never independently typed. Mirrors
// Rezerv's own tiered UI: set a tier's min to 5 and the previous tier
// automatically reads "1 - 4," with no separate max field to keep in sync
// by hand — overlapping/inconsistent bands become structurally unreachable
// instead of something the form has to validate against.
const maxForTier = (tiers: { min_value: number | null }[], index: number): number | null =>
  index < tiers.length - 1 ? (tiers[index + 1].min_value ?? 0) - 1 : null;

const tierRangeLabel = (tiers: { min_value: number | null }[], index: number, axis: "attendees" | "revenue"): string => {
  const min = tiers[index].min_value ?? 0;
  const max = maxForTier(tiers, index);
  if (axis === "revenue") {
    return max === null ? `${formatCurrency(min)}+` : `${formatCurrency(min)} - ${formatCurrency(max)}`;
  }
  const suffix = translate("payrollPage.rates.tier_range_attendee_suffix");
  return max === null ? `${min}+ ${suffix}` : `${min} - ${max} ${suffix}`;
};

// Skips any pair involving a band that hasn't been filled in yet (min_value
// still null) — an incomplete band isn't "out of order" relative to its
// neighbor, it's just not typed yet, so comparing it as if it were 0 flagged
// false positives (e.g. clicking "+ Tambah Tingkat" twice in a row, before
// typing into the first new band, incorrectly blocked the second add and
// surfaced the order-violation message even though no field was ever shown
// red — isOutOfOrder below has the same null exemption for this reason).
// handleSaveAll separately blocks a genuinely incomplete band at Save time,
// with its own distinct message.
const validateTierOrder = (tiers: { min_value: number | null }[]): string | null => {
  for (let i = 1; i < tiers.length; i++) {
    const prev = tiers[i - 1].min_value;
    const curr = tiers[i].min_value;
    if (prev == null || curr == null) continue;
    if (curr <= prev) {
      return translate("payrollPage.rates.tier_order_error");
    }
  }
  return null;
};

// Fixed-width columns shared by the tier-header row AND every instructor
// row below it, so they line up vertically like a real grid — without ever
// becoming an AntD Table (no grid-lines, no header sort/filter chrome, no
// per-row expand). This is what a card-list layout needs to read as "put
// together" rather than scattered: the columns must actually align.
const NAME_COL = 220;
const TIER_COL = 130;
const VALUE_COL = 200;
const ACTION_COL = 32;
const COL_GAP = 16;

const rosterGridTemplate = (isTiered: boolean, bandCount: number): string =>
  isTiered
    ? `${NAME_COL}px repeat(${Math.max(bandCount, 1)}, ${TIER_COL}px) ${ACTION_COL}px`
    : `${NAME_COL}px ${VALUE_COL}px ${ACTION_COL}px`;

// A tier's min-value box deliberately renders as a plain AntD <Input> (raw
// text), not an <InputNumber> with a custom formatter/parser. Several
// attempts at making InputNumber's own empty-value handling behave — a
// custom parser returning undefined/"", then an onInput override forcing a
// null — all traced correctly through rc-input-number's source but still
// didn't stop an emptied box from silently refilling with "0" in practice
// (0 is this field's own real, common value, unlike every other currency
// field here, whose formatter hides 0 as blank and so never surfaces
// whatever the library's internal empty-decimal resolution actually does).
// A plain text input sidesteps that machinery entirely: WE own the string,
// strip everything but digits, and treat "no digits left" as unambiguously
// null — no library-internal ambiguity left to fight.
const formatTierMinValue = (value: number | null, axis: "attendees" | "revenue"): string => {
  if (value == null) return "";
  return axis === "revenue" ? formatCurrency(value) : String(value);
};
const parseTierMinValue = (text: string): number | null => {
  const digits = text.replace(/\D/g, "");
  return digits === "" ? null : parseInt(digits, 10);
};

// The class's shared tier bands — defined ONCE for the whole class (not per
// instructor), which is what actually makes a tiered UI read as simple:
// every instructor below just fills in a dollar amount under these same
// bands, no nested per-row editor. Renders as the header row of the shared
// grid, directly above the matching column in every instructor row.
const TierBandsEditor = ({
  bands,
  axis,
  onChange,
  error,
  onOrderError,
}: {
  bands: ClassTierDraft[];
  axis: "attendees" | "revenue";
  onChange: (bands: ClassTierDraft[]) => void;
  error?: string;
  onOrderError: (message: string | undefined) => void;
}) => {
  // Starts blank, not auto-seeded to the previous tier's min + 1 — a guessed
  // number just had to be deleted before the real one could be typed in.
  // focusIndexRef records which new band should receive focus once it
  // actually exists in the DOM (the ref for it doesn't exist yet at the
  // moment addBand fires, since `bands` hasn't re-rendered) — the effect
  // below runs right after that render and focuses it, so typing can start
  // immediately without an extra click into the box.
  const focusIndexRef = useRef<number | null>(null);
  const inputRefs = useRef<Array<{ focus: () => void } | null>>([]);
  // A band only turns red once it's been "confirmed" — blurred, Enter
  // pressed, or an add-tier/Simpan attempt revealed it — not on every
  // keystroke while still mid-typing (a half-typed "345" isn't wrong yet,
  // it's just not "10001" YET).
  const [touchedIndexes, setTouchedIndexes] = useState<Set<number>>(new Set());
  const touchIndex = (index: number) => setTouchedIndexes((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
  const touchAll = () => setTouchedIndexes(new Set(bands.map((_, i) => i)));
  // The instant an already-red field is edited again, it goes back to plain
  // black — re-typing a number shouldn't keep showing the OLD verdict live,
  // flickering red/black with every keystroke. It only gets re-touched (and
  // possibly turns red again) on the next confirm, same as a fresh field.
  const untouchIndex = (index: number) =>
    setTouchedIndexes((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  // Simpan's own validateTierOrder failure (handleSaveAll, surfaced here via
  // the `error` prop) should reveal every out-of-order field too — otherwise
  // a field the user never blurred still looks valid even though Simpan
  // just refused to save because of it.
  useEffect(() => {
    if (error) touchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  // The bottom summary message is stale the instant the bands it was about
  // stop being invalid — e.g. deleting the one offending band — so clear it
  // in lockstep with `bands` rather than only ever on the next Simpan/Tambah
  // Tingkat attempt.
  useEffect(() => {
    if (!validateTierOrder(bands)) onOrderError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bands]);
  // Confirming a field (blur/Enter) is the only thing allowed to bring the
  // hint back — editing (see the InputNumber onChange below, which clears it
  // immediately) never does, so it can't flicker back on while still typing.
  const confirmIndex = (index: number) => {
    touchIndex(index);
    const orderError = validateTierOrder(bands);
    if (orderError) onOrderError(orderError);
  };
  const addBand = () => {
    // Mirrors handleSaveAll's own guard: a new tier can't be added on top of
    // an already-broken order — reveal the offending field(s), AND the same
    // bottom summary message Simpan would show, instead of silently adding
    // another box that would just be wrong too.
    const orderError = validateTierOrder(bands);
    if (orderError) {
      touchAll();
      onOrderError(orderError);
      return;
    }
    focusIndexRef.current = bands.length;
    onChange([...bands, { id: null, min_value: null }]);
  };
  useEffect(() => {
    if (focusIndexRef.current === null) return;
    inputRefs.current[focusIndexRef.current]?.focus();
    focusIndexRef.current = null;
  }, [bands]);
  // A remove shifts every later band's index down by one, which would
  // otherwise leave touchedIndexes pointing at the wrong bands (a touched,
  // invalid tier 4 becomes the new tier 3, silently carrying its "show red"
  // flag onto a box that was never actually confirmed) — clearing it here
  // means every remaining/new box starts untouched again, exactly like a
  // fresh add.
  const removeBand = (index: number) => {
    onChange(bands.filter((_, i) => i !== index));
    setTouchedIndexes(new Set());
  };
  const updateBandMin = (index: number, min: number | null) =>
    onChange(bands.map((b, i) => (i === index ? { ...b, min_value: min } : b)));
  const minLabel = translate(axis === "revenue" ? "payrollPage.rates.tier_min_revenue_label" : "payrollPage.rates.tier_min_label");

  return (
    <Flex vertical gap={10}>
      <Flex align="flex-start" gap={COL_GAP}>
        {/* Only the name-column spacer + tier columns here — no trailing
            action-column spacer, since "+ Tambah Tingkat" sits right after
            the last tier box as a flex sibling (matching Rezerv), not
            aligned under the roster rows' delete-icon column below. */}
        <div style={{ display: "grid", gridTemplateColumns: `${NAME_COL}px repeat(${bands.length}, ${TIER_COL}px)`, columnGap: COL_GAP }}>
          <div />
          {bands.map((band, index) => {
            // Out-of-order bands are flagged in place (red text) instead of
            // being silently clamped up to the previous tier's min + 1 — a
            // user who types e.g. "5000" into tier 3 when tier 2 is "10000"
            // almost certainly meant a different tier, and auto-correcting
            // it to "10001" behind their back hid the mistake rather than
            // catching it. handleSaveAll's validateTierOrder still blocks
            // Simpan the same way (see the tier_order_error message below).
            // Only shown once touched (see touchIndex/touchAll above), AND
            // only once a real number is actually sitting in the box —
            // blurring a still-EMPTY box (e.g. clicking away mid-edit to fix
            // a different tier) touches it too, and treating an absent value
            // as "0" for the order check flagged it as red even though
            // there's no actual wrong number visible to explain why: just an
            // empty box with a red cursor and nothing to point at.
            const isOutOfOrder =
              index > 0 &&
              touchedIndexes.has(index) &&
              band.min_value != null &&
              band.min_value <= (bands[index - 1].min_value ?? 0);
            return (
              <Flex key={index} vertical gap={4}>
                <Flex align="center" justify="space-between" gap={4}>
                  <Typography.Text type="secondary" style={{ fontSize: textPx(11) }}>
                    {minLabel}
                  </Typography.Text>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => removeBand(index)} />
                </Flex>
                <Input
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  style={{ width: "100%" }}
                  // `style` targets the outer wrapper, not the real <input>
                  // inside it, so a plain color style here never reaches the
                  // typed digits — the className below (index.css) recolors
                  // the text only, border left normal.
                  className={isOutOfOrder ? "tier-min-invalid" : undefined}
                  inputMode="numeric"
                  value={formatTierMinValue(band.min_value, axis)}
                  onChange={(e) => {
                    untouchIndex(index);
                    onOrderError(undefined);
                    updateBandMin(index, parseTierMinValue(e.target.value));
                  }}
                  onBlur={() => confirmIndex(index)}
                  onPressEnter={() => confirmIndex(index)}
                />
                <Typography.Text type="secondary" style={{ fontSize: textPx(11) }}>
                  {tierRangeLabel(bands, index, axis)}
                </Typography.Text>
              </Flex>
            );
          })}
        </div>
        {/* Top-aligned against the grid, with an invisible spacer the same
            size as each tier column's label row above it — so the button
            lines up exactly with the InputNumber row, not the vertical
            center of the whole (label + input + caption) column. */}
        <Flex vertical gap={4}>
          <div style={{ visibility: "hidden" }}>
            <Button size="small" icon={<DeleteOutlined />} />
          </div>
          {/* No icon here — the label already carries its own "+", matching
              every other add affordance in this feature ("+ Tambah Instruktur"
              etc.); pairing that with PlusOutlined rendered as a literal
              double plus. */}
          <Button onClick={addBand}>{translate("payrollPage.rates.add_tier")}</Button>
        </Flex>
      </Flex>
      {error && (
        <Typography.Text type="danger" style={{ fontSize: textPx(12) }}>
          {error}
        </Typography.Text>
      )}
    </Flex>
  );
};

type RosterRow = { instructor_id: string; full_name: string; rate?: PayrollRate };

// One roster row as a plain card-list row (avatar + name, then either a
// single flat-Rp amount or one input per shared tier band) instead of a
// table row — replaces the old Table + expandable-row TierEditor. The
// single-input branch is only ever reached by Per Kelas / Per Peserta >
// Rata, both flat Rupiah — Per Pendapatan is always tiered now (see
// isTieredLike/isPercentTier in ClassCommissionEditor), so there's no
// percent-vs-currency choice left to make on the single-input row.
const InstructorRow = ({
  row,
  isTiered,
  isPercentTier,
  bands,
  amountDraft,
  tierAmountDrafts,
  onAmountChange,
  onTierAmountChange,
  onDelete,
  highlightEmptyBands,
}: {
  row: RosterRow;
  isTiered: boolean;
  isPercentTier: boolean;
  bands: ClassTierDraft[];
  amountDraft: number | null;
  tierAmountDrafts: Record<number, number | null>;
  onAmountChange: (value: number | null) => void;
  onTierAmountChange: (bandIndex: number, value: number | null) => void;
  onDelete: () => void;
  // Set by a refused Simpan: every box still blank is outlined red until it
  // holds a number. Rp 0 is a value (rateAmountInputProps shows it as such).
  highlightEmptyBands: boolean;
}) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: rosterGridTemplate(isTiered, bands.length),
      columnGap: COL_GAP,
      alignItems: "center",
      padding: "12px 0",
      borderBottom: `1px solid ${colors.line}`,
    }}
  >
    <Flex align="center" gap={10}>
      <InstructorAvatar instructorId={row.instructor_id} name={row.full_name} />
      <Typography.Text strong style={{ fontSize: textPx(13) }}>
        {row.full_name}
      </Typography.Text>
    </Flex>
    {isTiered ? (
      bands.map((_, index) => (
        <InputNumber
          key={index}
          {...(isPercentTier ? percentInputProps : rateAmountInputProps)}
          style={{ width: "100%" }}
          value={tierAmountDrafts[index] ?? undefined}
          status={highlightEmptyBands && tierAmountDrafts[index] == null ? "error" : undefined}
          onChange={(v) => onTierAmountChange(index, v as number)}
        />
      ))
    ) : (
      <InputNumber
        {...rateAmountInputProps}
        style={{ width: "100%" }}
        value={amountDraft ?? undefined}
        status={highlightEmptyBands && amountDraft == null ? "error" : undefined}
        onChange={(v) => onAmountChange(v as number)}
      />
    )}
    <Flex justify="center">
      <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={onDelete} />
    </Flex>
  </div>
);

// One "Simpan" for the whole roster (and, under Tiered, the shared bands
// above it too) — not one per instructor row. Every row's edits (a
// Flat/RevenuePercent amount, or a Tiered per-band amount) live as local
// drafts here until Simpan is pressed, which saves band changes first (if
// any), then every changed instructor row against the resulting band ids,
// all in one go. Deleting an existing rate stays immediate (it's a
// distinct, already-committed action, not a draft to batch).
const ClassCommissionEditor = ({
  classTypeId,
  currentMode,
  currentStructure,
  hasExistingSetting,
  rates,
  classTiers,
  staffOptions,
  includeDeparted,
  onIncludeDepartedChange,
}: {
  classTypeId: string;
  currentMode: CommissionMode | null;
  currentStructure: CommissionRateStructure | null;
  hasExistingSetting: boolean;
  rates: PayrollRate[];
  classTiers: PayrollClassTier[];
  staffOptions: { value: string; label: string }[];
  includeDeparted: boolean;
  onIncludeDepartedChange: (value: boolean) => void;
}) => {
  // The Tabs are always visible (no separate "Ubah Struktur" edit-mode
  // toggle) — picking a different tab is itself how you change the
  // structure, same principle as dropping the old "Simpan the structure
  // before you can even see the roster" gate. Defaults to whatever's
  // currently live so the page opens on the class's real structure, not
  // always "Per Kelas." Lazy-initialized once: this component only ever
  // mounts after the class detail has already loaded (the page shows a
  // spinner until then), so currentMode/currentStructure are already
  // correct at mount time — no resync-on-reopen concern since there's no
  // open/close cycle anymore.
  const [topChoice, setTopChoice] = useState<TopChoice>(topChoiceOf(currentMode, currentStructure) ?? "per_class");
  const [attendeeStructure, setAttendeeStructure] = useState<CommissionRateStructure>(
    currentStructure === CommissionRateStructure.Tiered ? CommissionRateStructure.Tiered : CommissionRateStructure.Flat,
  );
  // "Per Pendapatan" itself only ever shows two top-level pills (Persentase /
  // Bertingkat) — RevenuePercentTiered is NOT a third sibling pill, and
  // "Persentase" is no longer a single flat-input choice either: it ALWAYS
  // renders the same shared tier-band grid Bertingkat uses (percent inputs
  // instead of Rupiah), starting from one Rp0+ band, exactly matching
  // Rezerv's "By Sales" tab (its tier grid is always present under one tab,
  // no separate flat mode). No user-facing toggle needed for this.
  const [revenueTopChoice, setRevenueTopChoice] = useState<"percent" | "tiered">(
    currentStructure === CommissionRateStructure.RevenueTiered ? "tiered" : "percent",
  );
  const revenuePercentStructure =
    revenueTopChoice === "tiered" ? CommissionRateStructure.RevenueTiered : CommissionRateStructure.RevenuePercentTiered;

  const resolvedMode = topChoice === "per_class" ? CommissionMode.PerClass : CommissionMode.PerAttendee;
  const resolvedStructure =
    topChoice === "per_class" ? CommissionRateStructure.Flat : topChoice === "per_revenue" ? revenuePercentStructure : attendeeStructure;
  const structureChanged = hasExistingSetting && (resolvedMode !== currentMode || resolvedStructure !== currentStructure);
  // A class still holding an old flat RevenuePercent rate (from before
  // Persentase became always-tiered) silently upgrades to RevenuePercentTiered
  // the moment its class is opened — that's not a user-chosen structure
  // switch, so unlike every other structure change it must NOT blank the
  // roster or show the "existing data will be cleared" warning below: the
  // getTierAmountDraft fallback further down shows the old flat amount as
  // this synthetic first band's value, and Simpan just re-persists that same
  // value in the new tiered shape (the setting itself still needs updating
  // server-side, so structureChanged above stays true for that purpose).
  const isLegacyPercentUpgrade =
    currentStructure === CommissionRateStructure.RevenuePercent && resolvedStructure === CommissionRateStructure.RevenuePercentTiered;

  // Tiered, RevenueTiered, and RevenuePercentTiered all render the shared
  // multi-band roster UI — just bucketed by a different axis (attendee count
  // vs revenue Rupiah) and, for RevenuePercentTiered, with each band's input
  // being a percent instead of a flat Rp amount (isPercentTier).
  const isTieredLike =
    resolvedStructure === CommissionRateStructure.Tiered ||
    resolvedStructure === CommissionRateStructure.RevenueTiered ||
    resolvedStructure === CommissionRateStructure.RevenuePercentTiered;
  const isPercentTier = resolvedStructure === CommissionRateStructure.RevenuePercentTiered;
  const tierAxis: "attendees" | "revenue" =
    resolvedStructure === CommissionRateStructure.RevenueTiered || resolvedStructure === CommissionRateStructure.RevenuePercentTiered
      ? "revenue"
      : "attendees";

  // A structure the user just picked can't keep showing the OLD structure's
  // saved rates/tiers — a flat Rp amount means nothing reinterpreted as a
  // tiered band or a revenue percentage. Blank drafts here; Simpan clears
  // the real data server-side (with a warning) to match — except the silent
  // legacy-percent upgrade above, which keeps showing the real data as-is.
  const effectiveRates = structureChanged && !isLegacyPercentUpgrade ? [] : rates;
  const effectiveClassTiers = structureChanged && !isLegacyPercentUpgrade ? [] : classTiers;

  const [pendingInstructorIds, setPendingInstructorIds] = useState<string[]>([]);
  const [amountDrafts, setAmountDrafts] = useState<Record<string, number | null>>({});
  // tierAmountDrafts[instructor_id][bandIndex] = amount
  const [tierAmountDrafts, setTierAmountDrafts] = useState<Record<string, Record<number, number | null>>>({});
  const [bandsOverride, setBandsOverride] = useState<ClassTierDraft[] | null>(null);
  const [bandsError, setBandsError] = useState<string | undefined>();
  // Roster-level refusal: some instructor still has a band without an
  // amount above 0. Shown under the roster and as red outlines on the boxes.
  const [rosterError, setRosterError] = useState<string | undefined>();

  // Every local draft above is only meaningful for whichever structure is
  // currently selected — none of them were reset on a tab switch, so editing
  // tier bands under one axis (e.g. Per Peserta > Bertingkat, attendee
  // counts), then switching to a different tiered structure (e.g. Per
  // Pendapatan > Bertingkat, Rupiah) WITHOUT saving first, kept showing and
  // re-editing the same stale bands as if they belonged to the new
  // structure — Simpan would then persist attendee-count numbers as revenue
  // thresholds. Clearing on every resolvedStructure change (this also fires
  // once, harmlessly, on mount, since everything is already empty then).
  useEffect(() => {
    setPendingInstructorIds([]);
    setAmountDrafts({});
    setTierAmountDrafts({});
    setBandsOverride(null);
    setBandsError(undefined);
    setRosterError(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedStructure]);

  const { mutate: deleteRate } = useDeletePayrollRate();
  const { mutateAsync: upsertSettingAsync, isPending: isSavingSetting } = useUpsertClassCommissionSetting();
  const { mutateAsync: upsertRateAsync, isPending: isSavingRates } = useUpsertPayrollRate();
  const { mutateAsync: upsertTiersAsync, isPending: isSavingTiers } = useUpsertClassCommissionTiers();

  // Bands are derived from the live class tiers by default (not stored
  // separately) so a successful save — which clears bandsOverride — always
  // snaps back to the freshly-saved server state rather than stale local
  // state. A brand-new Tiered/RevenueTiered class with no bands yet seeds
  // one empty band immediately, the shared-band equivalent of "expanding a
  // row pops out a tier" from the old per-instructor design.
  const liveBands: ClassTierDraft[] = effectiveClassTiers.map((t) => ({ id: t.id, min_value: t.min_value }));
  const bands = bandsOverride ?? (liveBands.length > 0 ? liveBands : isTieredLike ? [{ id: null, min_value: 0 }] : []);

  const hasExistingTierAmounts = effectiveRates.some((r) => r.tier_amounts.length > 0);
  const bandsChanged =
    isTieredLike && bands.map((b) => b.min_value).join(",") !== effectiveClassTiers.map((t) => t.min_value).join(",");

  const usedInstructorIds = new Set([...effectiveRates.map((r) => r.instructor_id), ...pendingInstructorIds]);
  const rosterRows: RosterRow[] = [
    ...effectiveRates.map((r) => ({ instructor_id: r.instructor_id, full_name: r.instructor.user.full_name, rate: r })),
    ...pendingInstructorIds
      .filter((id) => !effectiveRates.some((r) => r.instructor_id === id))
      .map((id) => ({ instructor_id: id, full_name: staffOptions.find((s) => s.value === id)?.label ?? "" })),
  ];

  // The single-input branch is only ever Flat (Per Kelas / Per Peserta >
  // Rata) now — always a raw Rp integer, no percent scaling.
  const getAmountDraft = (row: RosterRow): number | null => {
    if (row.instructor_id in amountDrafts) return amountDrafts[row.instructor_id];
    return row.rate?.amount ?? null;
  };
  const getTierAmountDraft = (row: RosterRow, bandIndex: number): number | null => {
    const rowOverride = tierAmountDrafts[row.instructor_id];
    if (rowOverride && bandIndex in rowOverride) return rowOverride[bandIndex];
    if (!row.rate) return null;
    const band = bands[bandIndex];
    if (band?.id) {
      const stored = row.rate.tier_amounts.find((ta) => ta.tier_id === band.id)?.amount ?? null;
      if (stored == null) return null;
      // RevenuePercentTiered tier amounts are stored scaled x100 for two
      // decimal places (e.g. 225 = 2.25%) — see the payroll_commission_
      // rate_tier_amount.amount comment in schema.prisma.
      return isPercentTier ? stored / 100 : stored;
    }
    // No real band yet (band.id is null) means this row hasn't been saved
    // against a real tier — EXCEPT the legacy-percent-upgrade case: an old
    // flat RevenuePercent rate has no tier_amounts at all, just its single
    // `amount`. Surface that value as this synthetic first (and only) band
    // so opening the class doesn't look like the configured rate vanished.
    if (isPercentTier && bandIndex === 0 && bands.length === 1 && row.rate.amount != null && row.rate.tier_amounts.length === 0) {
      return row.rate.amount / 100;
    }
    return null;
  };

  // Removing an instructor — whether deleting an already-saved rate or
  // dropping a not-yet-saved pending row — must also drop any local draft
  // for them. Drafts are deliberately never cleared on save (see doSave
  // below), so without this, re-adding the SAME instructor later in the
  // same session would resurrect their old amount instead of starting blank.
  const clearInstructorDrafts = (instructorId: string) => {
    setAmountDrafts((prev) => {
      const { [instructorId]: _removed, ...rest } = prev;
      return rest;
    });
    setTierAmountDrafts((prev) => {
      const { [instructorId]: _removed, ...rest } = prev;
      return rest;
    });
  };

  // A new row starts blank under every structure: Simpan refuses a blank box
  // (handleSaveAll), so the amount is always a conscious entry, Rp 0 included.
  const handleAddInstructor = (instructorId: string) => {
    setPendingInstructorIds((ids) => [...ids, instructorId]);
  };

  const doSave = async () => {
    // mutateAsync re-throws after its own onError runs (which already shows
    // an error toast via getApiErrorMessage — see hooks/mutations/payroll.ts)
    // — without this try/catch, that rejection propagated out of doSave
    // unhandled (an async function's caller here never awaits/catches it),
    // crashing into React's uncaught-error overlay instead of just leaving
    // the toast on screen. A partial save is safe to leave as-is: every step
    // below is itself an independent soft-delete-then-recreate write, so
    // re-pressing Simpan after fixing the offending value is a normal retry.
    try {
      let savedSomething = false;

      if (!hasExistingSetting || structureChanged) {
        await upsertSettingAsync({ classTypeId, mode: resolvedMode, rate_structure: resolvedStructure });
        savedSomething = true;
      }

      let effectiveBands = bands;
      if (isTieredLike && bandsChanged) {
        const saved = await upsertTiersAsync({
          classTypeId,
          tiers: bands.map((b, i) => ({ min_value: b.min_value ?? 0, max_value: maxForTier(bands, i) })),
        });
        effectiveBands = saved.map((t) => ({ id: t.id, min_value: t.min_value }));
        savedSomething = true;
      }

      // A saved row is written whenever it was edited, exactly as shown: a
      // cleared band or amount is stored as 0 (so the list's status and the
      // next payroll run agree with the screen). After a band change EVERY
      // saved tiered row is rewritten too: the server retires the old band
      // rows, so an amount left pointing at them would silently stop paying;
      // rewriting re-points each amount (by band position) at the new bands.
      // A not-yet-saved row is written once it holds a value
      // (handleSaveAll refuses a blank box first).
      const wasEdited = (row: RosterRow) => row.instructor_id in amountDrafts || row.instructor_id in tierAmountDrafts;
      const hasAnyValue = (row: RosterRow) =>
        isTieredLike ? bands.some((_, index) => getTierAmountDraft(row, index) !== null) : getAmountDraft(row) !== null;
      const rowsToSave = rosterRows.filter((row) =>
        row.rate ? wasEdited(row) || (isTieredLike && bandsChanged) : hasAnyValue(row),
      );
      savedSomething = savedSomething || rowsToSave.length > 0;
      await Promise.all(
        rowsToSave.map((row) => {
          if (isTieredLike) {
            const tier_amounts = effectiveBands
              .map((band, index) => {
                const draft = getTierAmountDraft(row, index) ?? 0;
                // x100 scale (two decimal places), see getTierAmountDraft above.
                return { tier_id: band.id, amount: isPercentTier ? Math.round(draft * 100) : draft };
              })
              .filter((ta): ta is { tier_id: string; amount: number } => !!ta.tier_id);
            return upsertRateAsync({ instructor_id: row.instructor_id, class_type_id: classTypeId, amount: null, tier_amounts });
          }
          const amount = getAmountDraft(row);
          return upsertRateAsync({
            instructor_id: row.instructor_id,
            class_type_id: classTypeId,
            amount: amount ?? 0,
            tier_amounts: [],
          });
        }),
      );
      // Deliberately NOT clearing pendingInstructorIds/amountDrafts/
      // tierAmountDrafts here. They hold exactly what was just persisted, so
      // there's nothing to "clear" — clearing them right after the awaited
      // mutations resolve gambled on the parent's refetched rates/classTiers
      // props already being in place by the next render, which React Query
      // doesn't guarantee (the cache update and this component's own setState
      // aren't the same commit): for a beat there was neither a local draft
      // nor confirmed-fresh server data to read from, so the just-saved
      // numbers visibly disappeared. Once real data does show up in `rates`,
      // pendingInstructorIds' own filter already drops the now-redundant
      // pending entry (see rosterRows above), so nothing double-renders.
      // bandsOverride DOES get refreshed (not cleared) — to the same values,
      // just swapping in the real tier ids — so a second Simpan in this same
      // session builds tier_amounts against live ids instead of the
      // placeholder `id: null` a brand-new band starts with.
      if (isTieredLike) setBandsOverride(effectiveBands);
      // One toast for the whole Simpan click, not one per underlying
      // mutation — the setting/tiers/rate hooks all stay toast-silent (see
      // hooks/mutations/payroll.ts) specifically so this is the only
      // success message shown, regardless of how many instructor rows
      // just got saved. Skipped entirely if nothing was actually written
      // (e.g. Simpan pressed with no edits) — a real write is required
      // before claiming success.
      if (savedSomething) {
        getApiSuccessMessage(`${translate("payrollPage.rates.name")} ${translate("messages.success.update")}`);
      }
    } catch {
      // Swallow: the failing mutation's own onError already surfaced a toast.
    }
  };

  const handleSaveAll = () => {
    if (isTieredLike) {
      // Three distinct, accurately-worded failure states — a blank tier list
      // and an in-progress (not yet typed) band aren't "out of order", so
      // reusing tier_order_error for them read as nonsensical.
      const error =
        bands.length === 0
          ? translate("payrollPage.rates.tier_empty_error")
          : bands.some((b) => b.min_value == null)
            ? translate("payrollPage.rates.tier_incomplete_error")
            : validateTierOrder(bands);
      if (error) {
        setBandsError(error);
        return;
      }
      setBandsError(undefined);
    }
    // Every box must hold a number for every row, saved or pending: each
    // band under a tiered structure, the single amount under a flat one. A
    // blank box would silently pay Rp0 with no trace on the page; a typed
    // Rp 0 is a deliberate choice and is shown as such (rateAmountInputProps).
    const incomplete = rosterRows.some((row) =>
      isTieredLike ? bands.some((_, index) => getTierAmountDraft(row, index) == null) : getAmountDraft(row) == null,
    );
    if (incomplete) {
      setRosterError(translate("payrollPage.rates.tier_amount_required"));
      return;
    }
    setRosterError(undefined);
    // One combined warning: either the structure itself changed away from a
    // structure that had real configured data (checked against the REAL
    // server rates/tiers, not the already-blanked effective* ones), or the
    // structure is unchanged but the shared tier bands were edited while
    // amounts already existed against them. The silent legacy-percent
    // upgrade is exempt — nothing is actually being cleared there.
    if (structureChanged && !isLegacyPercentUpgrade && (rates.length > 0 || classTiers.length > 0)) {
      showConfirmModal({
        tone: "amber",
        title: translate("payrollPage.rates.change_structure_confirm_title"),
        content: translate("payrollPage.rates.change_structure_confirm_content"),
        confirmLabel: translate("payrollPage.rates.change_structure_confirm_button"),
        onConfirm: doSave,
      });
      return;
    }
    if (!structureChanged && isTieredLike && bandsChanged && hasExistingTierAmounts) {
      showConfirmModal({
        tone: "amber",
        title: translate("payrollPage.rates.change_tiers_confirm_title"),
        content: translate("payrollPage.rates.change_tiers_confirm_content"),
        confirmLabel: translate("payrollPage.rates.change_tiers_confirm_button"),
        onConfirm: doSave,
      });
      return;
    }
    doSave();
  };

  // Tabs (Rezerv-style: "By Sales / By Class Attendees / By Class" across the
  // top) instead of a stacked Radio.Group list. Per Peserta's own Flat/Tiered
  // choice renders as a pill toggle inside that tab, mirroring Rezerv's
  // "Percent / Fixed amount" toggle under its "By Sales" tab.
  const structureItems: TabsProps["items"] = [
    {
      key: "per_class",
      label: translate("payrollPage.rates.mode_per_class"),
      children: (
        <Typography.Text style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink }}>
          {translate("payrollPage.rates.structure_per_class_hint")}
        </Typography.Text>
      ),
    },
    {
      key: "per_attendee",
      label: translate("payrollPage.rates.mode_per_attendee"),
      children: (
        <Flex vertical gap={8} align="flex-start">
          <ConfigProvider theme={pillSegmentedTheme}>
            <Segmented
              value={attendeeStructure}
              onChange={(value) => setAttendeeStructure(value as CommissionRateStructure)}
              options={[
                { label: translate("payrollPage.rates.structure_flat"), value: CommissionRateStructure.Flat },
                { label: translate("payrollPage.rates.structure_tiered"), value: CommissionRateStructure.Tiered },
              ]}
            />
          </ConfigProvider>
          <Typography.Text style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink }}>
            {attendeeStructure === CommissionRateStructure.Flat
              ? translate("payrollPage.rates.structure_flat_attendee_hint")
              : translate("payrollPage.rates.structure_tiered_hint")}
          </Typography.Text>
        </Flex>
      ),
    },
    {
      key: "per_revenue",
      label: translate("payrollPage.rates.mode_revenue"),
      children: (
        <Flex vertical gap={8} align="flex-start">
          <ConfigProvider theme={pillSegmentedTheme}>
            <Segmented
              value={revenueTopChoice}
              onChange={(value) => setRevenueTopChoice(value as "percent" | "tiered")}
              options={[
                { label: translate("payrollPage.rates.structure_revenue_percent"), value: "percent" },
                { label: translate("payrollPage.rates.structure_tiered"), value: "tiered" },
              ]}
            />
          </ConfigProvider>
          <Typography.Text style={{ fontSize: textPx(14), fontWeight: 500, color: colors.ink }}>
            {revenueTopChoice === "tiered"
              ? translate("payrollPage.rates.structure_revenue_tiered_hint")
              : translate("payrollPage.rates.structure_revenue_percent_tiered_hint")}
          </Typography.Text>
          {/* Applies to both revenue structures, so it sits on its own line
              that stays put while the hint above swaps with the toggle.
              Visible text rather than a tooltip: it has to be read while
              choosing, and a hover can't be reached on a tablet. */}
          <Typography.Text style={{ fontSize: textPx(13), color: colors.ink2 }}>
            {translate("payrollPage.rates.structure_revenue_excluded_note")}
          </Typography.Text>
        </Flex>
      ),
    },
  ];

  return (
    <InstructorColorProvider>
      <Flex vertical gap={20}>
        <Tabs activeKey={topChoice} items={structureItems} onChange={(key) => setTopChoice(key as TopChoice)} />
        {/* One soft-bordered card wraps the tier headers AND every instructor
            row together — same fixed grid columns throughout — so the whole
            block reads as one cohesive unit instead of scattered floating
            pieces. */}
        <Flex
          vertical
          gap={16}
          style={{
            border: `1px solid ${colors.line}`,
            borderRadius: 14,
            padding: "18px 20px",
            background: colors.bgSoft,
            width: "fit-content",
          }}
        >
          {isTieredLike && (
            <>
              <Typography.Text strong style={{ fontSize: textPx(13) }}>
                {translate(
                  tierAxis === "revenue" ? "payrollPage.rates.tier_bands_section_label_revenue" : "payrollPage.rates.tier_bands_section_label",
                )}
              </Typography.Text>
              {/* Keyed on the resolved structure so a switch (e.g. Per
                  Peserta > Bertingkat to Per Pendapatan > Bertingkat) fully
                  remounts this editor — its internal touchedIndexes
                  otherwise survives the switch and could carry a stale
                  "show red" flag onto a band index that belongs to a
                  completely different structure now. */}
              <TierBandsEditor
                key={resolvedStructure}
                bands={bands}
                axis={tierAxis}
                onChange={setBandsOverride}
                error={bandsError}
                onOrderError={setBandsError}
              />
              <div style={{ height: 1, background: colors.line }} />
            </>
          )}
          <Flex vertical gap={0}>
            {rosterRows.length === 0 && (
              <Typography.Text type="secondary" style={{ fontSize: textPx(13), padding: "4px 0" }}>
                {translate("payrollPage.rates.empty_roster")}
              </Typography.Text>
            )}
            {rosterRows.map((row) => (
              <InstructorRow
                key={row.instructor_id}
                row={row}
                isTiered={isTieredLike}
                isPercentTier={isPercentTier}
                bands={bands}
                amountDraft={getAmountDraft(row)}
                // Resolved per band (local draft, else the stored tier amount),
                // the same way amountDraft is. Passing the raw draft map here
                // left every saved tier rate looking empty on load.
                tierAmountDrafts={Object.fromEntries(bands.map((_, index) => [index, getTierAmountDraft(row, index)]))}
                onAmountChange={(value) => setAmountDrafts((prev) => ({ ...prev, [row.instructor_id]: value }))}
                onTierAmountChange={(bandIndex, value) =>
                  setTierAmountDrafts((prev) => ({
                    ...prev,
                    [row.instructor_id]: { ...prev[row.instructor_id], [bandIndex]: value },
                  }))
                }
                highlightEmptyBands={!!rosterError}
                onDelete={() => {
                  if (row.rate) {
                    showDestructiveConfirm({
                      title: translate("payrollPage.rates.delete_confirm_title"),
                      message: translate("payrollPage.rates.delete_confirm_content"),
                      confirmLabel: translate("payrollPage.rates.row_delete"),
                      onConfirm: () => {
                        deleteRate(row.rate!.id);
                        clearInstructorDrafts(row.instructor_id);
                      },
                    });
                  } else {
                    setPendingInstructorIds((ids) => ids.filter((id) => id !== row.instructor_id));
                    clearInstructorDrafts(row.instructor_id);
                  }
                }}
              />
            ))}
            {rosterError && (
              <Typography.Text type="danger" style={{ fontSize: textPx(12), paddingTop: 10 }}>
                {rosterError}
              </Typography.Text>
            )}
          </Flex>
        </Flex>
        {/* Same row as the picker it actually filters, clustered together
            on the left rather than pushed apart — this row spans the full
            page width, so an earlier justify="space-between" attempt
            stretched the toggle all the way to the right edge with a large
            empty gap in between, reading as disconnected from the controls
            it's actually related to. A plain gap keeps everything as one
            visual group. */}
        <Flex align="center" gap={24}>
          <Flex align="center" gap={12}>
            <Select
              // Remounted on every pick (key) — with showSearch, antd's internal
              // search-box text otherwise survives across renders even though
              // `value` is always undefined, leaving stale typed text sitting in
              // the box after each add instead of resetting to the placeholder.
              key={pendingInstructorIds.length}
              style={{ width: 280 }}
              showSearch
              optionFilterProp="label"
              placeholder={translate("payrollPage.rates.add_instructor_placeholder")}
              options={staffOptions.filter((s) => !usedInstructorIds.has(s.value))}
              onChange={handleAddInstructor}
            />
            <ConfigProvider theme={modalButtonsTheme}>
              <Button type="primary" loading={isSavingSetting || isSavingRates || isSavingTiers} onClick={handleSaveAll}>
                {translate("payrollPage.rates.row_save")}
              </Button>
            </ConfigProvider>
          </Flex>
          <Flex align="center" gap={8}>
            <Switch size="small" checked={includeDeparted} onChange={onIncludeDepartedChange} />
            <Typography.Text style={{ fontSize: textPx(13) }}>{translate("payrollPage.run_start.include_departed")}</Typography.Text>
          </Flex>
        </Flex>
      </Flex>
    </InstructorColorProvider>
  );
};

const ClassCommissionPage = () => {
  const { classTypeId } = useParams<{ classTypeId: string }>();
  const navigate = useNavigate();
  // Every write on this page needs the payroll_setting row (see
  // assertPayrollSettingExists on the server), so a fresh studio that lands
  // here by direct URL / back-navigation gets the same "save settings
  // first" state Run Payroll shows, instead of an editor whose Simpan can
  // only fail.
  const { data: settings, isLoading: isSettingsLoading } = useQuery(payrollSettingsOptions());
  const { data: classes = [], isLoading: isClassesLoading } = useQuery(payrollClassesOptions());
  const { data: detail, isLoading: isDetailLoading } = useQuery(classCommissionDetailOptions(classTypeId));
  // Hidden by default, same as Run Payroll's own toggle — actively assigning
  // a NEW commission rate to someone who's already departed isn't a normal
  // case, but being able to look one up for a correction/audit still is.
  const [includeDeparted, setIncludeDeparted] = useState(false);
  const { data: staff = [] } = useQuery(payrollStaffOptions(includeDeparted));
  const { mutate: duplicateSetting } = useDuplicateClassCommissionSetting();

  const classInfo = classes.find((c) => c.class_type_id === classTypeId);
  const setting = detail?.setting ?? null;
  const rates = detail?.rates ?? [];
  const classTiers = detail?.tiers ?? [];
  // Instructors only: the payroll roster also lists non-teaching Staf, who
  // never appear on a schedule, so a commission rate for them would be dead
  // configuration (the server refuses it as well).
  const staffOptions = staff
    .filter((s) => s.user.roles.includes(UserType.Instructor))
    .map((s) => ({ value: s.user_id, label: s.user.full_name }));

  // Grouped under "Private"/"Open" headers, matching the same class picker
  // pattern used by the "Buat Jadwal Baru" drawer (ClassSection.tsx) — a
  // group is only pushed when it actually has classes in it, so an empty
  // category never renders a blank header.
  const duplicateClassOptions = useMemo(() => {
    const targets = classes.filter((c) => c.class_type_id !== classTypeId);
    const privateClasses = targets.filter((c) => c.category === ClassCategory.Private);
    const openClasses = targets.filter((c) => c.category === ClassCategory.Open);
    const options: { label: string; title: string; options: { label: string; value: string }[] }[] = [];
    if (privateClasses.length > 0) {
      options.push({
        label: "Private",
        title: "Private",
        options: privateClasses.map((c) => ({ label: c.name, value: c.class_type_id })),
      });
    }
    if (openClasses.length > 0) {
      options.push({
        label: "Open",
        title: "Open",
        options: openClasses.map((c) => ({ label: c.name, value: c.class_type_id })),
      });
    }
    return options;
  }, [classes, classTypeId]);

  const openDuplicatePicker = () => {
    let targetIds: string[] = [];
    showConfirmModal({
      title: translate("payrollPage.rates.duplicate_title"),
      content: (
        <Flex vertical gap={10}>
          <Typography.Text type="secondary" style={{ fontSize: textPx(13) }}>
            {translate("payrollPage.rates.duplicate_desc")}
          </Typography.Text>
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            showSearch
            optionFilterProp="label"
            options={duplicateClassOptions}
            onChange={(value) => {
              targetIds = value;
            }}
          />
        </Flex>
      ),
      confirmLabel: translate("payrollPage.rates.duplicate_confirm"),
      tone: "amber",
      onConfirm: () => {
        if (targetIds.length && classTypeId) {
          duplicateSetting({ classTypeId, target_class_type_ids: targetIds });
        }
      },
    });
  };

  // Muted + regular weight so it reads as a breadcrumb back-link, not a
  // second bold heading competing with the class name for attention.
  // Typography.Title's ambient bold/size otherwise bleeds onto every child,
  // including this button's label. On its own row (not inline with the class
  // name) so the actual page title gets its own line, between this
  // breadcrumb and the structure Tabs below. Shared by the settings-required
  // state and the editor below.
  const backToSettingsButton = (
    <Button
      type="text"
      icon={<ArrowLeftOutlined />}
      onClick={() => navigate(PATHS.PAYROLL)}
      style={{ paddingLeft: 0, color: colors.ink2, fontWeight: 400 }}
    >
      {translate("payrollPage.rates.back_to_settings")}
    </Button>
  );

  // Settings loading is part of the spinner so a direct URL never flashes
  // the editor for a fresh studio before the guard below can resolve.
  if (isSettingsLoading || isClassesLoading || isDetailLoading) {
    return (
      <Flex justify="center" style={{ padding: 60 }}>
        <Spin />
      </Flex>
    );
  }

  if (settings && !settings.exists) {
    return (
      <PayrollSettingsRequired
        heading={backToSettingsButton}
        description={translate("payrollPage.rates.empty_settings_desc")}
      />
    );
  }

  if (!classInfo || !classTypeId) {
    navigate(PATHS.PAYROLL);
    return null;
  }

  return (
    <>
      <PageHeading title={backToSettingsButton} />

      <Flex align="center" justify="space-between" gap={16} wrap="wrap" style={{ marginBottom: 20 }}>
        <Flex align="center" gap={12}>
          <Typography.Text strong style={{ fontSize: textPx(18) }}>
            {classInfo.name}
          </Typography.Text>
          <ClassTypeBadge type={classInfo.category} />
        </Flex>
        {setting && (
          <Button icon={<CopyOutlined />} onClick={openDuplicatePicker}>
            {translate("payrollPage.rates.duplicate_to_classes_cta")}
          </Button>
        )}
      </Flex>

      <ClassCommissionEditor
        classTypeId={classTypeId}
        currentMode={setting?.mode ?? null}
        currentStructure={setting?.rate_structure ?? null}
        hasExistingSetting={!!setting}
        rates={rates}
        classTiers={classTiers}
        staffOptions={staffOptions}
        includeDeparted={includeDeparted}
        onIncludeDepartedChange={setIncludeDeparted}
      />
    </>
  );
};

export default ClassCommissionPage;
